import { useSEO } from '@/hooks/useSEO'
import PageHero from '@/components/ui/PageHero'

// Patreon — LIVE. Exact anchor referenced in the podcast episodes.
const PATREON_URL = 'https://patreon.com/thearchivenetwork'

// Buy Me a Coffee — LIVE.
const BUYMEACOFFEE_URL = 'https://buymeacoffee.com/thearchivenetwork'

// Stripe one-off donation — LIVE.
const STRIPE_DONATE_URL = 'https://buy.stripe.com/8x214o7NO3RW3q4eZ07AI0a'

type Rail = {
  name: string
  blurb: string
  cta: string
  href: string
  comingSoon?: boolean
  icon: JSX.Element
}

const rails: Rail[] = [
  {
    name: 'Patreon',
    blurb: 'Chip in monthly. The same page linked from every podcast episode.',
    cta: 'Support on Patreon',
    href: PATREON_URL,
    icon: (
      <svg className="w-6 h-6 text-archive-gold" fill="currentColor" viewBox="0 0 24 24">
        <path d="M14.82 2.41c3.96 0 7.18 3.24 7.18 7.21 0 3.96-3.22 7.18-7.18 7.18-3.97 0-7.21-3.22-7.21-7.18 0-3.97 3.24-7.21 7.21-7.21M2 21.6h3.5V2.41H2V21.6Z" />
      </svg>
    ),
  },
  {
    name: 'Buy Me a Coffee',
    blurb: 'A one-off coffee — the quickest way to say thanks.',
    cta: 'Buy a coffee',
    href: BUYMEACOFFEE_URL,
    icon: (
      <svg className="w-6 h-6 text-archive-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8h13v5a4 4 0 01-4 4H7a4 4 0 01-4-4V8zm13 1h2.5a2.5 2.5 0 010 5H16M6 3v2m4-2v2" />
      </svg>
    ),
  },
  {
    name: 'One-off donation',
    blurb: 'A single contribution of any amount, by card via Stripe.',
    cta: 'Donate once',
    href: STRIPE_DONATE_URL,
    icon: (
      <svg className="w-6 h-6 text-archive-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h2m-4 4h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
]

export default function SupportPage() {
  useSEO({
    title: 'Support',
    description:
      'The Archive Network is a solo-run, independent project. Every archive, podcast, and documentary is free — and donations are pure patronage: they unlock nothing, they just keep the work going.',
    url: 'https://thearchivenetwork.com/support',
  })

  return (
    <div className="animate-fade-in">
      <PageHero
        image="/images/covers/support.jpg"
        title="Keep the network free"
        subtitle="The Archive Network is a solo-run, independent project — twelve archives and a growing library of podcasts and documentaries, all free and open. No ads, no logins to read, no paywalls on the archives."
        variant="full"
      />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {/* Honest framing */}
        <div className="artifact-frame mx-auto max-w-3xl p-8 text-center">
          <p className="leading-relaxed text-archive-text">
            <span className="font-medium text-archive-cream">Donations don't unlock anything.</span>{' '}
            There are no donor perks and no donor tiers — the archives, podcasts, and documentaries
            stay free for everyone, whether you give or not. What support does is keep the lights on
            and fund the work: research, writing, production, hosting, and new archives. If the network
            has been worth your time, a contribution helps keep it that way.
          </p>
        </div>

        {/* Speak with History is the network's one paid product — distinct from donations. */}
        <p className="mx-auto mt-6 max-w-3xl text-center text-sm leading-relaxed text-archive-muted">
          One thing here isn't free, and it isn't a donation either:{' '}
          <span className="text-archive-cream">Speak with History</span>, the interactive AI feature, is
          free to try and offers an optional <span className="text-archive-cream">Historian</span>{' '}
          subscription. That's a subscription to a product, not a gift to the network — supporting here
          unlocks nothing there, and subscribing there isn't a donation here.
        </p>

        {/* Donation rails */}
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {rails.map((rail) => (
            <div key={rail.name} className="artifact-frame flex flex-col p-6">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-archive-gold/10">
                {rail.icon}
              </div>
              <h2 className="font-display text-xl text-archive-cream">{rail.name}</h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-archive-muted">{rail.blurb}</p>
              <a
                href={rail.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={rail.comingSoon ? true : undefined}
                className={`mt-6 w-full rounded px-4 py-2 text-center text-sm font-semibold transition-colors ${
                  rail.comingSoon
                    ? 'cursor-not-allowed border border-archive-gold/30 text-archive-muted'
                    : 'bg-archive-gold text-archive-black hover:bg-archive-amber'
                }`}
                onClick={rail.comingSoon ? (e) => e.preventDefault() : undefined}
              >
                {rail.cta}
              </a>
              {rail.comingSoon && (
                <p className="mt-2 text-center text-xs text-archive-muted">Link coming soon</p>
              )}
            </div>
          ))}
        </div>

        {/* Footnote */}
        <p className="mx-auto mt-12 max-w-2xl text-center text-sm text-archive-muted">
          Prefer another way to help? Sharing an episode, leaving a review, or telling a friend
          does just as much to keep the archives growing.
        </p>
      </div>
    </div>
  )
}
